import { useLayoutEffect, useRef, type RefObject } from 'react'
import { num } from './geometry'
import { motionOn } from './motion'
import { STRETCH, stretchEdges } from './springs'

/* `pill stretch` for a shared indicator behind unequal pills (the top bar, the admin sidebar) or equal cells (the phone tab
   bars): the leading edge travels on the snappy spring (260 ms, a 3% overshoot), the trailing edge on the settle spring
   (380 ms), so mid-move the indicator stretches and it lands tight (APP-DESIGN-DIRECTION 3.7). One clip-path cannot carry
   two easings, so the frames are sampled. Geometry is measured in physical pixels, so right-to-left needs no special case. */

interface Geometry { left: number; right: number }

/** Sampled frames from one measured geometry to another. Exported for tests. `axis: 'y'` reads left/right as top/bottom. */
export function indicatorFrames(from: Geometry, to: Geometry, round: string, axis: 'x' | 'y' = 'x'): Array<{ clipPath: string }> {
  const forward = to.left > from.left || (to.left === from.left && to.right < from.right)
  const frames: Array<{ clipPath: string }> = []
  for (let ms = 0; ms <= STRETCH.total + 0.01; ms += STRETCH.total / 24) {
    const { lead, trail } = stretchEdges(ms)
    // Moving forward (towards larger `left`) the far edge leads; moving back the near edge leads.
    const left = from.left + (to.left - from.left) * (forward ? trail : lead)
    const right = from.right + (to.right - from.right) * (forward ? lead : trail)
    const a = num(Math.max(0, left))
    const b = num(Math.max(0, right))
    const tail = round ? ` round ${round}` : ''
    frames.push({ clipPath: axis === 'x' ? `inset(0 ${b}px 0 ${a}px${tail})` : `inset(${a}px 0 ${b}px 0${tail})` })
  }
  return frames
}

interface Options {
  containerRef: RefObject<HTMLElement | null>
  indicatorRef: RefObject<HTMLElement | null>
  /** Index among `itemSelector` matches of the active item, or -1 for none. */
  activeIndex: number
  itemSelector: string
  /** CSS `round` radius for the clip-path, e.g. `999px`, or empty for square. */
  round: string
  /** `y` for a vertical list (the admin sidebar). Default `x`. */
  axis?: 'x' | 'y'
  /** Re-measure (without a stretch) when this changes: the list was hidden or moved, as the admin phone menu is. */
  watch?: unknown
}

/** Positions the indicator over the active item and stretches it to the next one on change. */
export function useStretchIndicator({ containerRef, indicatorRef, activeIndex, itemSelector, round, axis = 'x', watch }: Options) {
  const last = useRef<Geometry | null>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    const indicator = indicatorRef.current
    if (!container || !indicator) return

    const measure = (): Geometry | null => {
      const item = container.querySelectorAll<HTMLElement>(itemSelector)[activeIndex]
      if (!item) return null
      if (axis === 'y') return { left: item.offsetTop, right: Math.max(0, container.clientHeight - item.offsetTop - item.offsetHeight) }
      return { left: item.offsetLeft, right: Math.max(0, container.clientWidth - item.offsetLeft - item.offsetWidth) }
    }
    const place = (geometry: Geometry | null, animate: boolean) => {
      if (!geometry) {
        indicator.style.visibility = 'hidden'
        last.current = null
        return
      }
      indicator.style.visibility = 'visible'
      const tail = round ? ` round ${round}` : ''
      const finalClip = axis === 'x' ? `inset(0 ${geometry.right}px 0 ${geometry.left}px${tail})` : `inset(${geometry.left}px 0 ${geometry.right}px 0${tail})`
      indicator.style.clipPath = finalClip
      if (animate && last.current && motionOn() && typeof indicator.animate === 'function'
        && (last.current.left !== geometry.left || last.current.right !== geometry.right)) {
        indicator.animate(indicatorFrames(last.current, geometry, round, axis), { duration: STRETCH.total, easing: 'linear' })
      }
      last.current = geometry
    }

    place(measure(), true)
    // A list that was just shown settles its layout over the next frames (fonts, a scrollbar): measure again then.
    const frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => place(measure(), false)) : 0
    const late = window.setTimeout(() => place(measure(), false), 400)
    if (typeof ResizeObserver === 'undefined') return () => { cancelAnimationFrame?.(frame); window.clearTimeout(late) }
    const observer = new ResizeObserver(() => place(measure(), false))
    observer.observe(container)
    return () => { observer.disconnect(); cancelAnimationFrame?.(frame); window.clearTimeout(late) }
  }, [containerRef, indicatorRef, activeIndex, itemSelector, round, axis, watch])
}
